/**
 * Documento ⇄ canvas. Cada card vira um nó (posição = x, y do card) e cada
 * ligação uma aresta que sai da alça da porta (`portHandleId`) e chega na
 * única alça de entrada do card. Os objetos têm o formato do @xyflow/react
 * sem depender dele — o domínio não conhece a biblioteca.
 */
import { parsePortHandle, portHandleId } from './blocks';
import { connect, locateBlock } from './document';
import type { DocGroup, FlowDoc } from './types';

/** A alça de entrada de todo card. */
export const TARGET_HANDLE = 'in';
export const CARD_NODE = 'card';

export interface CardNodeData extends Record<string, unknown> {
  group: DocGroup;
  isStart: boolean;
}

export interface CanvasNode {
  id: string;
  type: typeof CARD_NODE;
  position: { x: number; y: number };
  data: CardNodeData;
}

export interface CanvasEdge {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
}

export function toCanvasNodes(doc: FlowDoc): CanvasNode[] {
  return doc.groups.map((group) => ({
    id: group.id,
    type: CARD_NODE,
    position: { x: group.x, y: group.y },
    data: { group, isStart: group.id === doc.start_group_id },
  }));
}

/**
 * Bloco → id do card que o contém, montado uma vez por documento. Um id
 * repetido fica com o primeiro card, como em `locateBlock`.
 */
function blockOwners(doc: FlowDoc): Map<string, string> {
  const owners = new Map<string, string>();
  for (const group of doc.groups) {
    for (const block of group.blocks) {
      if (!owners.has(block.id)) owners.set(block.id, group.id);
    }
  }
  return owners;
}

/** Ligações com origem ou destino inexistente ficam fora do canvas (a validação as aponta). */
export function toCanvasEdges(doc: FlowDoc): CanvasEdge[] {
  const groups = new Set(doc.groups.map((g) => g.id));
  // Um índice em vez de `locateBlock` por ligação: 500 ligações × 600 blocos
  // a cada tecla viravam uma busca quadrática.
  const owners = blockOwners(doc);
  const out: CanvasEdge[] = [];
  for (const e of doc.edges) {
    const source = owners.get(e.from_block_id);
    if (source === undefined || !groups.has(e.to_group_id)) continue;
    out.push({
      id: e.id,
      source,
      sourceHandle: portHandleId(e.from_block_id, e.from_item_id),
      target: e.to_group_id,
      targetHandle: TARGET_HANDLE,
    });
  }
  return out;
}

/** Uma ligação puxada no canvas → o documento com ela (trocando a da mesma porta). */
export function applyConnection(
  doc: FlowDoc,
  connection: { source: string | null; sourceHandle?: string | null; target: string | null },
): FlowDoc {
  const port = parsePortHandle(connection.sourceHandle);
  if (!port || !connection.target) return doc;
  // A alça precisa ser de um bloco do card de onde a ligação saiu.
  if (locateBlock(doc, port.blockId)?.group.id !== connection.source) return doc;
  return connect(doc, port, connection.target);
}

/** As posições do canvas de volta ao documento (fim de um arrasto). */
export function applyPositions(
  doc: FlowDoc,
  positions: ReadonlyMap<string, { x: number; y: number }>,
): FlowDoc {
  if (positions.size === 0) return doc;
  return {
    ...doc,
    groups: doc.groups.map((g) => {
      const p = positions.get(g.id);
      return p ? { ...g, x: Math.round(p.x), y: Math.round(p.y) } : g;
    }),
  };
}
